import React, { useEffect, useState } from 'react';
import {
  DndContext,
  useDraggable,
  useDroppable,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Typography,
  Box,
} from '@mui/material';

function DraggableTeamSlot({ id, team }) {
  const {
    attributes,
    listeners,
    setNodeRef: setDraggableNodeRef,
    transform,
  } = useDraggable({ id });
  const { setNodeRef: setDroppableNodeRef, isOver } = useDroppable({ id });
  
  const combinedRef = (node) => {
    setDraggableNodeRef(node);
    setDroppableNodeRef(node);
  };
  
  const style = {
    transform: CSS.Translate.toString(transform),
    border: isOver ? '2px solid green' : '1px solid #999',
  };
  
  return (
    <Box
      component="div"
      ref={combinedRef}
      style={style}
      sx={{
        padding: 1,
        backgroundColor: '#fff',
        minWidth: '150px',
        textAlign: 'center',
      }}
      {...listeners}
      {...attributes}
    >
      <Typography variant="body2">
        {team ? team.name : 'N/A'}
      </Typography>
    </Box>
  );
}

function AvailablePlayer({ player, playType, participantType }) {
  
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: `available-${player.id}`,
  });
  const style = {
    transform: CSS.Translate.toString(transform),
    border: '1px solid #666',
    marginBottom: '8px',
  };
  
  const displayName =
    playType === 'Doubles' || participantType === 'Teams'
      ? (player.name || 'N/A')
      : `${player.firstName || ''} ${player.lastName || ''}`.trim() || 'N/A';
  
  return (
    <Box
      component="div"
      ref={setNodeRef}
      style={style}
      sx={{
        padding: 1,
        backgroundColor: '#eee',
        minWidth: '150px',
        textAlign: 'center',
      }}
      {...listeners}
      {...attributes}
    >
      <Typography variant="body2">{displayName}</Typography>
    </Box>
  );
}

function FillInRequiredItem() {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: 'available-fillIn',
  });
  const style = {
    transform: CSS.Translate.toString(transform),
    border: '1px solid #666',
    marginBottom: '8px',
  };
  return (
    <Box
      component="div"
      ref={setNodeRef}
      style={style}
      sx={{
        padding: 1,
        backgroundColor: '#eee',
        minWidth: '150px',
        textAlign: 'center',
      }}
      {...listeners}
      {...attributes}
    >
      <Typography variant="body2">Fill-In Required</Typography>
    </Box>
  );
}

function parseSlotId(id) {
  const parts = id.split('-');
  if (parts.length < 5) return null;
  const weekIndex = Number(parts[1]);
  const matchIndex = Number(parts[3]);
  const teamKey = parts[4];
  return { weekIndex, matchIndex, teamKey };
}

function EditDrawModal({
  open,
  onClose,
  draw,
  availablePlayers,
  participantType,
  playType,
  onSave,
}) {

  const [editedDraw, setEditedDraw] = useState(draw ? { ...draw } : { weeks: [] });

  useEffect(() => {
    if (draw) {
      setEditedDraw({ ...draw });
    }
  }, [draw]);

  const isLoading = !draw || !draw.weeks || draw.weeks.length === 0;

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    
    if (active.id.startsWith('available-')) {
      const availableId = active.id.replace('available-', '');
      const targetData = parseSlotId(over.id);
      if (!targetData) return;
      if (availableId === 'fillIn') {
        setEditedDraw((prev) => {
          const newWeeks = [...prev.weeks];
          const week = { ...newWeeks[targetData.weekIndex] };
          const matches = [...week.matches];
          matches[targetData.matchIndex] = {
            ...matches[targetData.matchIndex],
            [targetData.teamKey]: {
              id: `fillIn-${Date.now()}`,
              name: 'Fill-In Required',
              fillIn: true,
              section: '',
            },
          };
          week.matches = matches;
          newWeeks[targetData.weekIndex] = week;
          return { ...prev, weeks: newWeeks };
        });
      } else {

        const newPlayer = availablePlayers.find((p) => p.id === availableId);
        if (!newPlayer) return;
        const newTeam = { id: newPlayer.id, name: newPlayer.name };
        setEditedDraw((prev) => {
          const newWeeks = [...prev.weeks];
          const week = { ...newWeeks[targetData.weekIndex] };
          const matches = [...week.matches];
          matches[targetData.matchIndex] = {
            ...matches[targetData.matchIndex],
            [targetData.teamKey]: newTeam,
          };
          week.matches = matches;
          newWeeks[targetData.weekIndex] = week;
          return { ...prev, weeks: newWeeks };
        });
      }
    } else {

      const activeData = parseSlotId(active.id);
      const overData = parseSlotId(over.id);
      if (!activeData || !overData) return;
      if (activeData.weekIndex !== overData.weekIndex) return;
      setEditedDraw((prev) => {
        const newWeeks = [...prev.weeks];
        const week = { ...newWeeks[activeData.weekIndex] };
        const matches = [...week.matches];
        const temp = matches[activeData.matchIndex][activeData.teamKey];
        matches[activeData.matchIndex][activeData.teamKey] =
          matches[overData.matchIndex][overData.teamKey];
        matches[overData.matchIndex][overData.teamKey] = temp;
        week.matches = matches;
        newWeeks[activeData.weekIndex] = week;
        return { ...prev, weeks: newWeeks };
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Edit Draw</DialogTitle>
      <DialogContent sx={{ pb: 10 }}>
        {isLoading ? (
          <Typography>Loading draw data...</Typography>
        ) : (
          <DndContext onDragEnd={handleDragEnd}>
            <Box sx={{ display: 'flex', gap: 4 }}>

              <Box sx={{ flex: 1 }}>
                {editedDraw.weeks.map((week, weekIndex) => (
                  <Box key={weekIndex} sx={{ mb: 4 }}>
                    <Typography variant="h6">
                      Week {week.week} - {week.date}
                    </Typography>
                    {week.matches.map((match, matchIndex) => (
                      <Box
                        key={matchIndex}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          mb: 2,
                        }}
                      >
                        <DraggableTeamSlot
                          id={`week-${weekIndex}-match-${matchIndex}-team1`}
                          team={match.team1}
                        />
                        <Typography variant="body1">vs</Typography>
                        <DraggableTeamSlot
                          id={`week-${weekIndex}-match-${matchIndex}-team2`}
                          team={match.team2}
                        />
                      </Box>
                    ))}
                  </Box>
                ))}
              </Box>

              <Box sx={{ width: '200px' }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Available Players
                </Typography>
                {availablePlayers.map((player) => (
                  <AvailablePlayer
                    key={player.id}
                    player={player}
                    playType={playType}
                    participantType={participantType}
                  />
                ))}
                <FillInRequiredItem />
              </Box>
            </Box>
          </DndContext>
        )}
      </DialogContent>

      <Box
        sx={{
          position: 'sticky',
          bottom: 0,
          backgroundColor: 'background.paper',
          p: 2,
          textAlign: 'center',
          borderTop: '1px solid #ddd',
          display: 'flex',
          justifyContent: 'center',
          gap: 2,
        }}
      >
        <Button variant="outlined" onClick={onClose} color='error'>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            onSave(editedDraw);
            onClose();
          }}
        >
          Save Changes
        </Button>
      </Box>
    </Dialog>
  );
}

export default EditDrawModal;
